import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-describedby", "aria-required", "dateMarkerLegend", "defaultValue", "error", "id", "initialView", "locale", "name", "onChange", "showMilliseconds", "showSeconds", "showTime", "timePickerAriaLabels", "value", "weekStartDay"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import { describe } from '@amzn/stencil-react-desc';
import { StencilContext } from "../context";
import { Hr, View } from "../layout";
import { withTestAttributes } from "../utils";
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT } from "./constants";
import { DatePickerCalendar } from "./date-picker-calendar";
import { DatePickerWrapper } from "./helperComponents";
import { datePickerPropTypes } from "./propTypes";
import { TimePicker, TimeSegment } from "./time-picker";
import { getTimeSegmentsFromDateString } from "./time-picker/utils";
import { dateTimeToString, getDateFormat, getInitialView, getWeekStartDay, stringToDate } from "./utils";
import { jsx as ___EmotionJSX } from "@emotion/core";

var DatePickerWithoutDocs = function DatePickerWithoutDocs(_ref) {
  var ariaDescribedBy = _ref['aria-describedby'],
      ariaRequired = _ref['aria-required'],
      dateMarkerLegend = _ref.dateMarkerLegend,
      defaultValue = _ref.defaultValue,
      error = _ref.error,
      id = _ref.id,
      initialViewProp = _ref.initialView,
      locale = _ref.locale,
      name = _ref.name,
      onChange = _ref.onChange,
      showMilliseconds = _ref.showMilliseconds,
      showSeconds = _ref.showSeconds,
      showTime = _ref.showTime,
      timePickerAriaLabels = _ref.timePickerAriaLabels,
      value = _ref.value,
      weekStartDay = _ref.weekStartDay,
      internalProps = _objectWithoutProperties(_ref, _excluded);

  if (weekStartDay === undefined) {
    weekStartDay = getWeekStartDay(locale);
  }

  var _useContext = useContext(StencilContext),
      stencilLocale = _useContext.locale;

  if (!locale) {
    locale = stencilLocale;
  }

  var dateFormat = getDateFormat(locale);
  var formattedValue = value;
  var hasTimeInValue = false;

  if (value !== undefined) {
    hasTimeInValue = value.length > DEFAULT_DATE_FORMAT.length;

    if (hasTimeInValue) {
      formattedValue = dayjs(value).format(DEFAULT_DATE_TIME_FORMAT);
    }
  }

  var dateValue = formattedValue ? formattedValue.substr(0, dateFormat.length) : undefined;
  var timeValue = hasTimeInValue ? getTimeSegmentsFromDateString(formattedValue, showSeconds, showMilliseconds) : undefined;
  var initialSelection = stringToDate(dateValue || defaultValue);
  var initialView = getInitialView({
    initialSelection: initialSelection,
    initialViewProp: initialViewProp
  });

  var _useState = useState(initialSelection),
      _useState2 = _slicedToArray(_useState, 2),
      selectedDate = _useState2[0],
      setSelectedDate = _useState2[1];

  var _useState3 = useState(timeValue),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedTime = _useState4[0],
      setSelectedTime = _useState4[1];

  var _useState5 = useState(initialView),
      _useState6 = _slicedToArray(_useState5, 2),
      currentView = _useState6[0],
      setCurrentView = _useState6[1];

  var calendarDate = dateValue !== undefined ? initialSelection : selectedDate;
  var pickerTime = timeValue !== undefined && Object.keys(timeValue).length > 0 ? timeValue : selectedTime;

  var handleDateSelection = function handleDateSelection(date) {
    setSelectedDate(date);
    !!onChange && onChange(dateTimeToString(date, pickerTime));
    setCurrentView({
      month: date.getUTCMonth(),
      year: date.getUTCFullYear()
    });
  };

  var handleTimeSelection = function handleTimeSelection(time) {
    var _initialTime;

    var initialTime = (_initialTime = {}, _defineProperty(_initialTime, TimeSegment.Hours, 0), _defineProperty(_initialTime, TimeSegment.Minutes, 0), _initialTime);

    if (showSeconds) {
      initialTime[TimeSegment.Seconds] = 0;
    }

    if (showMilliseconds) {
      initialTime[TimeSegment.Milliseconds] = 0;
    }

    var newSelectedTime = _objectSpread(_objectSpread(_objectSpread({}, initialTime), pickerTime), time);

    setSelectedTime(newSelectedTime);
    !!onChange && onChange(dateTimeToString(calendarDate, newSelectedTime));
  }; // Update the view if controlled value changes


  useEffect(function () {
    var parsedDate = stringToDate(dateValue);

    if (parsedDate !== undefined) {
      var year = parsedDate.getUTCFullYear();
      var month = parsedDate.getUTCMonth();

      if (year !== currentView.year || month !== currentView.month) {
        setCurrentView({
          year: parsedDate.getUTCFullYear(),
          month: parsedDate.getUTCMonth()
        });
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [dateValue]);
  return ___EmotionJSX(DatePickerWrapper, {
    "data-test-component": "StencilDatePicker"
  }, (id || name) && ___EmotionJSX("input", {
    "aria-required": ariaRequired,
    type: "hidden",
    id: id,
    name: name,
    value: dateTimeToString(calendarDate, pickerTime)
  }), ___EmotionJSX(DatePickerCalendar, _extends({
    "aria-describedby": ariaDescribedBy,
    error: error,
    goToMonth: function goToMonth(month, year) {
      return setCurrentView({
        month: month,
        year: year
      });
    },
    locale: locale,
    month: currentView.month,
    onDateSelected: handleDateSelection,
    selectionStart: calendarDate,
    weekStartDay: weekStartDay,
    year: currentView.year
  }, internalProps)), dateMarkerLegend, showTime && ___EmotionJSX(React.Fragment, null, ___EmotionJSX(View, {
    padding: [0, 'S300']
  }, ___EmotionJSX(Hr, null)), ___EmotionJSX(TimePicker, {
    ariaLabels: timePickerAriaLabels,
    handleChange: handleTimeSelection,
    selectedTime: pickerTime,
    showMilliseconds: showMilliseconds,
    showSeconds: showSeconds
  })));
};

var DatePickerWithDocs = describe(DatePickerWithoutDocs).description('Interactive calendar for choosing dates.');
DatePickerWithDocs.propTypes = _objectSpread({}, datePickerPropTypes);
var DatePicker = withTestAttributes({})(DatePickerWithDocs);
DatePicker.displayName = 'DatePicker';
export default DatePicker;